import { defaultForgeCopy, defaultHomeForgeCopy } from "@loot-os/api-contract";
import type { PageTemplate } from "@loot-os/design-system/templates/contract";
import type {
  HomeContent,
  HomeForgeContent,
} from "@loot-os/design-system/templates/home/homeContent";
import { HOME_SECTIONS } from "@loot-os/design-system/templates/home/homeSections";
import { drawnVariant, type VariantOf } from "@loot-os/design-system/templates/sections";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useCurrency } from "../../../../lib/currency.js";
import { boxesQueryOptions, forgeBoxesInfiniteQueryOptions } from "../../../../lib/queries.js";
import { useTenant } from "../../../../lib/tenant.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { storefrontTemplateOf } from "../../../registry.js";
import { forgeCardOf } from "../../forge/useForgeContent.js";

/** How many boxes the stage's parade turns through. */
const PARADE_BOXES = 6;
/** How many items drop into each box on the stage: its own top item and the next boxes'. */
const ITEMS_PER_BOX = 3;
/** How many shoppers' boxes the showcase's rail carries. */
const SHOWCASE_BOXES = 12;
/** The one Forge variant that shows shoppers' boxes; renaming it in `HOME_SECTIONS` fails here. */
const SHOWCASE_VARIANT = "showcase" satisfies VariantOf<(typeof HOME_SECTIONS)["forge"]>;

/**
 * Whether the store's Home template draws the Forge as its showcase (its pick, or the section's
 * default), the one variant that shows shoppers' boxes: only then does Home ask for them, so a
 * template that draws the band or the stage costs no request.
 */
export function drawsForgeShowcase(template: PageTemplate<HomeContent>): boolean {
  return drawnVariant(HOME_SECTIONS, template.picks, "forge") === SHOWCASE_VARIANT;
}

/**
 * Home's pitch for the Forge, while the store has it on: build a box, open it or share it, and
 * earn up to the store's most commission when it pays any (`forge` on the config), with Create
 * and Browse. Its stage turns the store's boxes (GET /v1/boxes): each box's art, with the top
 * items of it and the boxes after it dropping in. Its showcase sets the newest boxes shoppers
 * built (GET /v1/forge/boxes, the Forge page's own first page) on a rail, asked for only when the
 * store's template draws it.
 */
export function useHomeForgeContent(): HomeForgeContent | undefined {
  const { config, hasFeature } = useTenant();
  const terms = useTerminology();
  const currency = useCurrency();
  const on = hasFeature("forge");
  const boxes = useQuery({ ...boxesQueryOptions(), enabled: on });
  const showcaseOn = on && drawsForgeShowcase(storefrontTemplateOf(config).home);
  const forged = useInfiniteQuery({
    ...forgeBoxesInfiniteQueryOptions("new"),
    enabled: showcaseOn,
  });
  const parade = useMemo(() => {
    const shown = (boxes.data ?? []).filter((box) => box.imageUrl).slice(0, PARADE_BOXES);
    const tops = shown.flatMap((box) => (box.topItem ? [box.topItem.imageUrl] : []));
    if (shown.length === 0 || tops.length === 0) return undefined;
    return shown.map((box, i) => ({
      image: box.imageUrl,
      items: Array.from(
        new Set(Array.from({ length: ITEMS_PER_BOX }, (_, k) => tops[(i + k) % tops.length] ?? "")),
      ),
    }));
  }, [boxes.data]);
  if (!on) return undefined;
  const forge = config.forge;
  const copy = defaultHomeForgeCopy(
    terms,
    forge?.commissionEnabled ? (forge.maxCreatorCutPercent ?? 0) : 0,
  );
  return {
    title: copy.title,
    body: copy.body,
    actions: [
      { label: copy.create, href: "/forge/create", emphasis: "primary" },
      { label: copy.browse, href: "/forge", emphasis: "secondary" },
    ],
    parade,
    boxes: showcaseOn
      ? {
          currency,
          // A failed load draws the band, as none built would: the pitch still stands alone.
          cards: forged.isPending
            ? null
            : (forged.data?.pages[0]?.data ?? [])
                .slice(0, SHOWCASE_BOXES)
                .map((box) => forgeCardOf(box, defaultForgeCopy(terms).grid)),
        }
      : undefined,
  };
}
